/**
 * Error-copy mapping — raw error string → user-facing friendly text.
 *
 * Two paths:
 *   1. 结构化 CortexBusinessError —— deck 侧 `deck/app/errors.py` 里定义，
 *      RunError event 的 content 形如
 *        __CORTEX_ERR__:{"error_type":"user_budget_exceeded","detail":"..."}
 *      按 error_type 查 COPY 表拿文案，detail 只进 console。
 *   2. 旧逻辑兜底 —— 历史 event / 网络层错误没走 marker，保留子串匹配。
 *
 * 新增 error_type 时，按 deck/app/errors.py 的常量在下面加对应文案。
 * error_type 命名**只加不改**，和 deck 一起冻结。
 *
 * Bundle invariant: pure — no runtime deps, shared by desktop + mobile.
 */

const CORTEX_ERR_MARKER = '__CORTEX_ERR__:'

const _ERROR_TYPE_COPY: Record<string, { zh: string; en: string }> = {
  // LiteLLM / auth
  user_budget_exceeded: {
    zh: '您的使用额度已用完，请升级套餐或等待下次重置。',
    en: 'Your usage limit has been reached. Please upgrade or wait for the reset.',
  },
  user_session_limit: {
    zh: '当前会话用量已达上限，请稍候再试（每 5 小时滚动重置）。',
    en: 'Session usage limit reached. Please try again later (resets on a rolling 5-hour window).',
  },
  free_task_limit: {
    zh: '本周 3 次任务已用完，下周一重置。升级套餐可继续使用。',
    en: 'All 3 Free tasks for this week have been used. They reset on Monday; upgrade to continue now.',
  },
  free_file_weekly_limit: {
    zh: '本周 1 次文件任务已用完，下周一重置。升级套餐可继续处理文件。',
    en: 'This week\'s Free file task has been used. It resets on Monday; upgrade to process more files.',
  },
  free_file_limit: {
    zh: 'Free 每次任务只能上传 1 个文件，请保留一个文件后重试。',
    en: 'Free supports one file per task. Keep one file attached and retry.',
  },
  usage_validation_unavailable: {
    zh: '暂时无法校验使用额度，请稍后重试。',
    en: 'Usage validation is temporarily unavailable. Please try again later.',
  },
  file_preparation_failed: {
    zh: '文件暂时无法准备，请重新上传后重试。此次不会消耗任务额度。',
    en: 'The file could not be prepared. Upload it again and retry; this did not use a task.',
  },
  free_file_size_limit: {
    zh: 'Free 单个文件不能超过 10 MB，请压缩后重试。',
    en: 'Free supports files up to 10 MB. Compress the file and retry.',
  },
  cowork_not_enabled: {
    zh: 'Cowork 需要订阅后才能使用。订阅 Cowork 即可解锁本地 LCA 工作流。',
    en: 'Cowork requires a subscription. Subscribe to unlock the local LCA workflow.',
  },
  rate_limit_hit: {
    zh: '请求太频繁或当前用量已达上限，请稍候片刻后重试。',
    en: 'Too many requests, or the current usage limit was reached. Please wait a moment and retry.',
  },
  invalid_api_key: {
    zh: '登录已过期，请重新登录。',
    en: 'Session expired, please log in again.',
  },
  session_expired: {
    zh: '登录已过期，请重新登录。',
    en: 'Session expired, please log in again.',
  },
  // Model provider
  context_window_exceeded: {
    zh: '消息太长，超出模型上下文窗口。建议新开对话或精简内容。',
    en: 'Message exceeds the model context window. Start a new conversation or shorten the input.',
  },
  model_overloaded: {
    zh: 'AI 服务暂时繁忙，请稍后重试。',
    en: 'The service is temporarily busy. Please retry shortly.',
  },
  model_unavailable: {
    zh: 'AI 服务暂时不可用（可能在维护或繁忙），请稍后重试。',
    en: 'The service is temporarily unavailable (maintenance or high load). Please try again later.',
  },
  model_response_invalid: {
    zh: '模型返回异常，请重试。',
    en: 'Model response was invalid. Please retry.',
  },
  // Agent / guardrails
  input_blocked: {
    zh: '请求被内容安全策略拦截。',
    en: 'Input blocked by content policy.',
  },
  output_blocked: {
    zh: '回复被内容安全策略拦截。',
    en: 'Response blocked by content policy.',
  },
  prompt_injection_detected: {
    zh: '检测到可疑的提示词注入，请求被拒绝。',
    en: 'Possible prompt injection detected — request rejected.',
  },
  pii_detected: {
    zh: '检测到敏感信息，请移除后重试。',
    en: 'Sensitive information detected. Please redact and retry.',
  },
  // Infra
  upstream_timeout: {
    zh: '上游响应超时，请稍后重试。',
    en: 'Upstream response timed out. Please retry.',
  },
  internal_error: {
    zh: '出了点问题，请稍后重试。',
    en: 'Something went wrong. Please try again later.',
  },
}

interface ParsedCortexError { error_type: string; detail?: string }

function parseCortexError(raw: string): ParsedCortexError | null {
  const idx = raw.indexOf(CORTEX_ERR_MARKER)
  if (idx < 0) return null
  const after = raw.slice(idx + CORTEX_ERR_MARKER.length)
  // marker 后**可能跟尾随文本** —— chat 走 deck SSE 保证结尾干净 JSON，但 cowork 走
  // Claude Agent SDK，SDK/fetch 常在 body 后追加 " . For more information, pass
  // verbose: true ..."。直接 JSON.parse(after) 会因尾巴抛错 → budget 漏判成 auth →
  // 摊原始 403 + 触发重登（issue #216）。按花括号配平截出 JSON 对象再 parse，
  // 跳过字符串字面量里的花括号。
  const start = after.indexOf('{')
  if (start < 0) return null
  let depth = 0, inStr = false, esc = false, end = -1
  for (let i = start; i < after.length; i++) {
    const ch = after[i]
    if (inStr) {
      if (esc) esc = false
      else if (ch === '\\') esc = true
      else if (ch === '"') inStr = false
    } else if (ch === '"') inStr = true
    else if (ch === '{') depth++
    else if (ch === '}') { if (--depth === 0) { end = i + 1; break } }
  }
  if (end < 0) return null
  try {
    const parsed = JSON.parse(after.slice(start, end))
    if (parsed && typeof parsed.error_type === 'string') return parsed as ParsedCortexError
  } catch { /* malformed payload — fall through to legacy matching */ }
  return null
}

// Legacy substring rules — for events without the marker (pre-migration
// deck releases, raw network errors, SDK timeouts that don't reach the
// classifier).
const _ERROR_RULES: Array<{ match: string[]; zh: string; en: string }> = [
  { match: ['stream ended before a result message'],
    zh: '回复意外中断，请重试。', en: 'The response was interrupted. Please try again.' },
  { match: ['401', 'unauthorized', 'authentication'],
    zh: '登录已过期，请重新登录。', en: 'Session expired, please re-login.' },
  { match: ['402', 'payment', 'credits', 'insufficient'],
    zh: '额度已用完，请升级套餐。', en: 'Credits exhausted, please upgrade your plan.' },
  { match: ['429', 'rate limit', 'too many'],
    zh: '请求过于频繁，请稍等片刻。', en: 'Too many requests. Please wait a moment.' },
  { match: ['500', 'internal server error', 'apierror'],
    zh: '服务暂时出了点问题，请稍后重试。', en: 'Service temporarily unavailable. Please try again later.' },
  { match: ['502', '503', '529', 'overloaded'],
    zh: '服务繁忙，请稍后重试。', en: 'Service is busy. Please try again later.' },
  { match: ['timeout', 'timed out', 'econnrefused', 'enotfound', 'network'],
    zh: '网络连接异常，请检查网络后重试。', en: 'Network error. Please check your connection and retry.' },
  { match: ['output token maximum', 'max_output_tokens', 'max output tokens'],
    zh: '任务太大超出了单次输出限制，建议拆成多个步骤分阶段推进。可以让 AI 先用任务列表规划，再逐步执行。',
    en: 'Task exceeded the single response limit. Try breaking it into smaller steps — ask the AI to plan with a task list first, then execute step by step.' },
]

export function friendlyError(raw: string, lang: 'zh' | 'en' = 'zh'): string {
  // Prefer the structured marker — stable across deck versions, no
  // substring false-matches, detail kept out of user-facing string.
  const parsed = parseCortexError(raw)
  if (parsed) {
    const copy = _ERROR_TYPE_COPY[parsed.error_type]
    if (copy) return copy[lang]
    // Unknown error_type — deck is newer than us. Log detail so the
    // gap surfaces in triage, then fall through to generic copy.
    console.warn('[cortexStream] unknown error_type from deck:', parsed.error_type, parsed.detail)
    return lang === 'zh' ? '出了点问题，请稍后重试。' : 'Something went wrong. Please try again later.'
  }
  const lower = raw.toLowerCase()
  for (const rule of _ERROR_RULES) {
    if (rule.match.some(k => lower.includes(k))) return rule[lang]
  }
  return lang === 'zh' ? '出了点问题，请稍后重试。' : 'Something went wrong. Please try again later.'
}

// error_type values that mean "the user is out of budget / needs to
// subscribe" — the chat error bubble pairs these with an upgrade CTA
// (→ Settings billing) instead of just the friendlyError text.
const _UPGRADE_ERROR_TYPES = new Set([
  'user_budget_exceeded', 'cowork_not_enabled', 'free_task_limit', 'free_file_weekly_limit',
])

/** Classify an error into a UI action hint (`CortexMessage.errorKind`).
 *  Marker-based only — legacy substring errors don't carry enough signal
 *  to safely offer an upgrade CTA, so they stay plain text. */
export function errorActionKind(raw: string): 'budget' | undefined {
  const parsed = parseCortexError(raw)
  if (parsed && _UPGRADE_ERROR_TYPES.has(parsed.error_type)) return 'budget'
  return undefined
}

// error_type values that genuinely mean "the user's auth failed and they must
// re-login". Only these pair with the cowork "re-login" CTA.
const _AUTH_ERROR_TYPES = new Set(['invalid_api_key', 'session_expired'])

/** Whether an error should trigger the "re-login" recovery action.
 *
 *  Marker-FIRST: if the error carries a `__CORTEX_ERR__` marker, trust its
 *  error_type — a non-auth type (e.g. `model_unavailable` from an upstream
 *  provider 403) must NOT trigger re-login even though the SDK wraps it as
 *  "API Error: 403 ...". Only fall back to the substring heuristic for raw /
 *  legacy errors that carry no marker (network errors, SDK timeouts). This
 *  fixes the upstream-403 (e.g. provider balance) → spurious re-login bug. */
export function isAuthError(raw: string): boolean {
  const parsed = parseCortexError(raw)
  if (parsed) return _AUTH_ERROR_TYPES.has(parsed.error_type)
  const lower = (raw || '').toLowerCase()
  return /401|403|authentication|unauthorized|failed to authenticate|登录已过期|登录状态异常|please re-login/.test(lower)
}
